'use client';

import { PortableTextSimple } from '@/components/portable-text/portable-text-simple';
import type { DesktopMenuProps } from '@/layout/navbar/navbar';
import { SearchButton } from '@/layout/navbar/search-button';
import { SearchButtonComponent } from '@/layout/navbar/search-button-component';
import { WishlistButton } from '@/layout/navbar/wishlist-button';
import { CustomLink } from '@veranda/shared/components/custom-link';
import { DesktopMenuShared } from '@veranda/shared/layout/navbar';
import { cn } from '@veranda/shared/lib';
import { SanityImage } from '@veranda/shared/sanity/utils/components';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { type ReactNode, Suspense } from 'react';
import { AccountButton } from './account-button';

const triggerClassName =
  'hf:after:bg-primary font-clash text-clash-body font-medium text-primary';

export function DesktopMenu({
  data,
  children,
}: {
  data: DesktopMenuProps;
  children: ReactNode;
}) {
  const pathname = usePathname();
  if (!data) return null;

  return (
    <DesktopMenuShared key={pathname} className="relative h-full">
      <DesktopMenuShared.Nav viewportClassName="shadow-md data-[state=open]:border-y data-[state=open]:border-primary/10">
        <DesktopMenuShared.Link
          href="/"
          className="-translate-y-1/2 absolute top-1/2 left-0"
        >
          <Image
            src="/images/logo.webp"
            alt="Logo"
            width={354}
            height={116}
            className="h-auto max-h-[54px] w-auto max-w-full"
          />
        </DesktopMenuShared.Link>

        <div className="flex h-full w-full items-center justify-center">
          {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation> */}
          {data.map((item) => {
            if (item.itemType === 'link') {
              return (
                <DesktopMenuShared.Link
                  key={item._key}
                  href={item.slug || ''}
                  openInNewTab={item.openInNewTab}
                  className={cn(triggerClassName)}
                >
                  {item.text}
                </DesktopMenuShared.Link>
              );
            }

            if (item.itemType === 'megaMenuGrid') {
              return (
                <DesktopMenuShared.Trigger
                  key={item.title}
                  title={item.title}
                  href={item.megaMenuTriggerLink?.slug || ''}
                  className={cn(triggerClassName)}
                >
                  <DesktopMenuShared.MegaMenu
                    columns={
                      item.links && item.links?.length > 3
                        ? item.links.length
                        : 4
                    }
                  >
                    {item.links?.map((navItem) => (
                      <DesktopMenuShared.Column key={navItem._key}>
                        <DesktopMenuShared.ColumnHeading className="typography-clash-h3 font-semibold text-primary">
                          {navItem.heading}
                        </DesktopMenuShared.ColumnHeading>
                        <DesktopMenuShared.ColumnItems>
                          {navItem.links?.map((subItem) => (
                            <DesktopMenuShared.GridItem
                              key={subItem._key}
                              href={subItem.link.slug || ''}
                              bgColor={subItem.settings?.bgColor || ''}
                              textColor={subItem.settings?.textColor || ''}
                              images={subItem.settings?.images}
                              className="font-medium text-xs"
                            >
                              {subItem.link.text}
                            </DesktopMenuShared.GridItem>
                          ))}
                        </DesktopMenuShared.ColumnItems>
                      </DesktopMenuShared.Column>
                    ))}
                  </DesktopMenuShared.MegaMenu>
                </DesktopMenuShared.Trigger>
              );
            }

            if (item.itemType === 'megaMenuHero') {
              return (
                <DesktopMenuShared.Trigger
                  key={item._key}
                  title={item.title}
                  href={item.megaMenuTriggerLink?.slug || ''}
                  className={cn(triggerClassName)}
                >
                  <DesktopMenuShared.MegaMenu columns={1}>
                    <DesktopMenuShared.Featured>
                      <DesktopMenuShared.FeaturedContent
                        imageCount={item.images?.length || 0}
                      >
                        {item.content && (
                          <div className="[&>h2]:typography-caslon-h2 grid gap-4 [&>*]:text-primary [&>h2]:text-balance [&>p]:text-balance">
                            <PortableTextSimple content={item.content} />
                          </div>
                        )}
                      </DesktopMenuShared.FeaturedContent>
                      {item.images && (
                        <DesktopMenuShared.FeaturedImageWrapper
                          imageCount={item.images?.length || 0}
                        >
                          {item.images.map((item) => {
                            if (item.itemType === 'image') {
                              return (
                                <div
                                  key={item._key}
                                  className="h-[400px] w-full max-w-lg max-md:hidden"
                                >
                                  <SanityImage image={item} />
                                </div>
                              );
                            }

                            if (item.itemType === 'imageWithLink') {
                              return (
                                <CustomLink
                                  key={item._key}
                                  href={item.link?.slug || ''}
                                  target={
                                    item.link?.openInNewTab ? '_blank' : '_self'
                                  }
                                  className="h-[400px] w-full max-w-lg max-md:hidden"
                                >
                                  <SanityImage image={item.image} />
                                </CustomLink>
                              );
                            }
                          })}
                        </DesktopMenuShared.FeaturedImageWrapper>
                      )}
                    </DesktopMenuShared.Featured>
                  </DesktopMenuShared.MegaMenu>
                </DesktopMenuShared.Trigger>
              );
            }
          })}
        </div>

        <div className="absolute right-0 flex items-center">
          <AccountButton />
          <WishlistButton />
          {children}
          <Suspense
            fallback={
              // biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
              <SearchButtonComponent isOpen={false} setIsOpen={() => {}} />
            }
          >
            <SearchButton />
          </Suspense>
        </div>
      </DesktopMenuShared.Nav>
    </DesktopMenuShared>
  );
}
